import { FUNNEL_URL } from '../config';

// Thin client for the funnel's existing auth APIs -- no auth logic here.
// Credentials sign-in replicates next-auth/react's own signIn() protocol by
// hand (csrf, then POST callback/credentials) since that SDK only runs
// inside a Next.js app; same endpoints, same session cookie either way.

async function getCsrfToken() {
  const res = await fetch(`${FUNNEL_URL}/api/auth/csrf`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Unable to reach the sign-in service.');
  const data = await res.json();
  return data.csrfToken;
}

export async function getFunnelSession() {
  const res = await fetch(`${FUNNEL_URL}/api/auth/session`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data && data.user ? data : null;
}

// Mirrors signIn("credentials", {redirect:false,...}) exactly. Returns
// {ok, error} -- error strings match the funnel's own (including the
// "EMAIL_VERIFICATION_REQUIRED:" prefix).
export async function credentialsSignIn({ email, password }) {
  const csrfToken = await getCsrfToken();
  const body = new URLSearchParams({
    email,
    password,
    csrfToken,
    callbackUrl: FUNNEL_URL,
    json: 'true',
  });

  const res = await fetch(`${FUNNEL_URL}/api/auth/callback/credentials`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    return { ok: false, error: 'Sign in failed. Please try again.' };
  }

  const data = await res.json().catch(() => ({}));
  let error = null;
  try {
    error = data.url ? new URL(data.url).searchParams.get('error') : null;
  } catch {
    error = null;
  }

  return { ok: !error, error };
}

export async function emailRoute(email) {
  const res = await fetch(`${FUNNEL_URL}/api/auth/email-route`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export async function signup({ name, email, password }) {
  const res = await fetch(`${FUNNEL_URL}/api/auth/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password, role: 'customer' }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export async function forgotPassword(email) {
  const res = await fetch(`${FUNNEL_URL}/api/auth/forgot-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export async function resendVerificationEmail(resendToken) {
  const res = await fetch(`${FUNNEL_URL}/api/auth/resend-verification-pre-auth`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resendToken }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}
